import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";

const sevColor = (s) => ({
  low: "bg-slate-100 text-slate-700",
  med: "bg-amber-100 text-amber-900",
  high: "bg-rose-100 text-rose-800",
}[s] || "bg-slate-100");

const AdminFraud = () => {
  const { t } = useLang();
  const [flags, setFlags] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resolveFor, setResolveFor] = useState(null);
  const [notes, setNotes] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/fraud/flags?resolved=${showResolved}`);
      setFlags(r.data.flags || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, "load failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [showResolved]);

  const resolve = async (id) => {
    try {
      await api.post(`/admin/fraud/flags/${id}/resolve`, null, { params: { notes: notes.slice(0, 200) } });
      toast.success("✔");
      setResolveFor(null);
      setNotes("");
      fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, "resolve failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-fraud-page">
      <div className="flex items-center gap-3">
        <ShieldAlert className="w-6 h-6 text-rose-600" />
        <h1 className="text-2xl font-bold text-slate-900" data-testid="admin-fraud-title">
          {t("referrals.admin.fraudHeading")}
        </h1>
        <label className="ms-auto inline-flex items-center gap-2 text-sm" data-testid="admin-fraud-show-resolved">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
          />
          {t("referrals.admin.showResolved")}
        </label>
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          {flags.length === 0 ? (
            <p className="p-6 text-slate-500 text-sm" data-testid="admin-fraud-empty">
              {t("referrals.admin.fraudEmpty")}
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-start text-xs uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-3">{t("referrals.admin.colRule")}</th>
                  <th className="py-2 px-3">{t("referrals.admin.colSeverity")}</th>
                  <th className="py-2 px-3">{t("referrals.admin.colSubject")}</th>
                  <th className="py-2 px-3">{t("referrals.admin.colNotes")}</th>
                  <th className="py-2 px-3">{t("referrals.admin.colActions")}</th>
                </tr>
              </thead>
              <tbody>
                {flags.map((f) => (
                  <tr key={f.id} className="border-b border-slate-100" data-testid={`admin-fraud-flag-${f.id}`}>
                    <td className="py-2 px-3 font-mono text-xs">{f.rule}</td>
                    <td className="py-2 px-3"><Badge className={sevColor(f.severity)}>{f.severity}</Badge></td>
                    <td className="py-2 px-3 font-mono text-xs text-slate-500">{(f.subject_id || "").slice(0, 8)}…</td>
                    <td className="py-2 px-3 text-xs text-slate-600 max-w-xs truncate">{f.notes || "—"}</td>
                    <td className="py-2 px-3">
                      {!f.resolved_at && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setResolveFor(f.id); setNotes(""); }}
                          data-testid={`admin-fraud-resolve-${f.id}`}
                        >
                          {t("referrals.admin.resolve")}
                        </Button>
                      )}
                      {f.resolved_at && (
                        <span className="text-xs text-emerald-700">✓ {f.resolved_at.slice(0, 10)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {resolveFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="admin-fraud-resolve-modal">
          <Card className="max-w-md w-full mx-4">
            <CardHeader><CardTitle>{t("referrals.admin.resolveModalTitle")}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Input
                placeholder={t("referrals.admin.resolveNotes")}
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 200))}
                maxLength={200}
                data-testid="admin-fraud-resolve-notes"
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setResolveFor(null)} data-testid="admin-fraud-resolve-cancel">
                  {t("referrals.admin.cancel")}
                </Button>
                <Button onClick={() => resolve(resolveFor)} className="bg-emerald-600 text-white" data-testid="admin-fraud-resolve-confirm">
                  {t("referrals.admin.confirmResolve")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="text-xs text-slate-400" data-testid="admin-fraud-loading">{loading ? "…" : ""}</div>
    </div>
  );
};

export default AdminFraud;
